import { Reply, RotateCcw } from 'lucide-react'
import PostEditor from '@/components/editor/PostEditor'
import VariableEditor from '@/components/editor/VariableEditor'

interface Agent {
  id: number
  name: string
  email: string
  avatar_url: string | null
  role: string
}

interface EmailPreviewProps {
  subject: string
  onSubjectChange: (value: string) => void
  body: string
  onBodyChange: (value: string) => void
  agent: Agent | undefined
  onImageUpload?: (file: File) => Promise<string>
}

function AgentAvatar({ agent }: { agent: Agent | undefined }) {
  if (agent?.avatar_url) {
    return <img src={agent.avatar_url} alt={agent.name} className="w-8 h-8 rounded-full object-cover" />
  }
  const initials = agent?.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'
  return (
    <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-medium text-xs">
      {initials}
    </div>
  )
}

export default function EmailPreview({
  subject,
  onSubjectChange,
  body,
  onBodyChange,
  agent,
  onImageUpload,
}: EmailPreviewProps) {
  return (
    <div className="flex flex-col h-full">
      {/* Browser Chrome - email client style (no URL bar) */}
      <div className="bg-gray-100 border-b border-gray-200 px-4 py-2.5 flex items-center gap-3 rounded-t-lg">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
        </div>
      </div>

      {/* Subject bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-2.5 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <VariableEditor
            value={subject}
            onChange={onSubjectChange}
            placeholder="Assunto do e-mail..."
            rows={1}
          />
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0 text-gray-400">
          <Reply className="w-4 h-4" />
          <RotateCcw className="w-4 h-4" />
        </div>
      </div>

      {/* Email body area */}
      <div className="flex-1 bg-gray-100 p-6 rounded-b-lg overflow-auto" style={{ minHeight: '400px' }}>
        {/* Email card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 max-w-[560px] mx-auto">
          {/* Body content */}
          <div className="p-6 text-sm text-gray-700">
            <PostEditor
              value={body}
              onChange={onBodyChange}
              placeholder="Escreva o conteúdo do e-mail..."
              minHeight="120px"
              onImageUpload={onImageUpload}
              hideButton
            />
          </div>
        </div>

        {/* Footer */}
        <div className="max-w-[560px] mx-auto mt-3">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <AgentAvatar agent={agent} />
              <span className="text-xs text-gray-600">
                {agent?.name || 'Agente'} &middot; Sua Empresa
              </span>
            </div>
            <span className="text-xs text-gray-400 hover:text-gray-500 cursor-default">
              Cancelar inscrição
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
